import React, { useCallback } from 'react'
import { ProfileAttributes } from '../../../types/profile/ProfileAttributes'
import { useTranslation } from 'react-i18next'
import AIRPNumberInput from '../../../components/common/AIRPNumberInput/AIRPNumberInput'
import { hasError, useFormErrorContext } from '../../../utils/context/FormErrorContext'
import { commaSeparate } from '../../../utils/formatter'

interface BedCountsTableProps {
    data: Record<string, number | null>;
    updateValue: (value: number | null, key: string) => void;
}

function BedCountsTable(props: BedCountsTableProps) {
    const { data, updateValue } = props
    const horizontalHeaders = ["classificationHeader", "AAA", "AA", "A", "B", "C", "D", "hTotalLicensedBedsHeader"]
    const verticalHeaders = ["licensedBedTypeHeader", "basicBedsHeader", "semiPrivateBedsHeader", "privateBedsHeader", "otherHeader", "vTotalLicensedBedsHeader"]
    const basicCellNames = ["basicBedAaa", "basicBedAa", "basicBedA", "basicBedB", "basicBedC", "basicBedD"]
    const semiCellNames = ["semiPrivateBedAaa", "semiPrivateBedAa", "semiPrivateBedA", "semiPrivateBedB", "semiPrivateBedC", "semiPrivateBedD"]
    const privateCellNames = ["privateBedAaa", "privateBedAa", "privateBedA", "privateBedB", "privateBedC", "privateBedD"]
    const otherCellNames = ["otherBedAaa", "otherBedAa", "otherBedA", "otherBedB", "otherBedC", "otherBedD"]
    const { errors } = useFormErrorContext()
    const { t } = useTranslation("ltc_profile_attribute")

    const calculateBasicBedTotal = useCallback(() => {
            return (data.basicBedAaa ?? 0)
                + (data.basicBedAa ?? 0)
                + (data.basicBedA ?? 0)
                + (data.basicBedB ?? 0)
                + (data.basicBedC ?? 0)
                + (data.basicBedD ?? 0)
        }, [data.basicBedAaa, data.basicBedAa, data.basicBedA, data.basicBedB, data.basicBedC, data.basicBedD])
        const calculateSemiPrivateBedTotal = useCallback(() => {
            return (data.semiPrivateBedAaa ?? 0)
                + (data.semiPrivateBedAa ?? 0)
                + (data.semiPrivateBedA ?? 0)
                + (data.semiPrivateBedB ?? 0)
                + (data.semiPrivateBedC ?? 0)
                + (data.semiPrivateBedD ?? 0)
        }, [data.semiPrivateBedAaa, data.semiPrivateBedAa, data.semiPrivateBedA, data.semiPrivateBedB, data.semiPrivateBedC, data.semiPrivateBedD])
        const calculatePrivateBedTotal = useCallback(() => {
            return (data.privateBedAaa ?? 0)
                + (data.privateBedAa ?? 0)
                + (data.privateBedA ?? 0)
                + (data.privateBedB ?? 0)
                + (data.privateBedC ?? 0)
                + (data.privateBedD ?? 0)
        }, [data.privateBedAaa, data.privateBedAa, data.privateBedA, data.privateBedB, data.privateBedC, data.privateBedD])

        const calculateOtherBedTotal = useCallback(() => {
            return (data.otherBedAaa ?? 0)
                + (data.otherBedAa ?? 0)
                + (data.otherBedA ?? 0)
                + (data.otherBedB ?? 0)
                + (data.otherBedC ?? 0)
                + (data.otherBedD ?? 0)
        }, [data.otherBedAaa, data.otherBedAa, data.otherBedA, data.otherBedB, data.otherBedC, data.otherBedD])

    const calculateAaaBedTotal = () => {
        return (data.basicBedAaa ?? 0)
            + (data.semiPrivateBedAaa ?? 0)
            + (data.privateBedAaa ?? 0)
            + (data.otherBedAaa ?? 0)
    }
    const calculateAaBedTotal = () => {
        return (data.basicBedAa ?? 0)
            + (data.semiPrivateBedAa ?? 0)
            + (data.privateBedAa ?? 0)
            + (data.otherBedAa ?? 0)
    }
    const calculateABedTotal = () => {
        return (data.basicBedA ?? 0)
            + (data.semiPrivateBedA ?? 0)
            + (data.privateBedA ?? 0)
            + (data.otherBedA ?? 0)
    }
    const calculateBBedTotal = () => {
        return (data.basicBedB ?? 0)
            + (data.semiPrivateBedB ?? 0)
            + (data.privateBedB ?? 0)
            + (data.otherBedB ?? 0)
    }
    const calculateCBedTotal = () => {
        return (data.basicBedC ?? 0)
            + (data.semiPrivateBedC ?? 0)
            + (data.privateBedC ?? 0)
            + (data.otherBedC ?? 0)
    }
    const calculateDBedTotal = () => {
        return (data.basicBedD ?? 0)
            + (data.semiPrivateBedD ?? 0)
            + (data.privateBedD ?? 0)
            + (data.otherBedD ?? 0)
    }

    const calculateTotal = () => {
        return calculateBasicBedTotal() + calculateSemiPrivateBedTotal() + calculatePrivateBedTotal() + calculateOtherBedTotal()
    }

    return (
        <>
            <table className='bed-counts-table table-with-border' id="bed-counts-table" role="presentation">
                <thead>
                    <tr>
                        <th rowSpan={1} id={verticalHeaders[0]} className="horizontal-header-borderless">{t(verticalHeaders[0])}</th>
                        <th colSpan={6} id={horizontalHeaders[0]} className="horizontal-header-borderless">{t(horizontalHeaders[0])}</th>
                        <th rowSpan={1} id={horizontalHeaders[7]} className="horizontal-header-borderless">{t(horizontalHeaders[7])}</th>
                    </tr>
                    <tr>
                        <td></td>
                        {horizontalHeaders.slice(1, 7).map((header, index) => <th key={`horizontal-header-${index}`} id={header} headers={horizontalHeaders[0] + " " + verticalHeaders[0]} className="horizontal-header">
                            {t(header)}
                        </th>)}
                        <td></td>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td id={verticalHeaders[1]} className="vertical-header">{t(verticalHeaders[1])}</td>
                        {basicCellNames.map((cellName, index) => <td key={`row-1-${index}`} className={`bed-counts-input-cell ${hasError(errors, cellName) ? "table-error" : ""}`} headers={horizontalHeaders.slice(1, 7)[index] + " " + verticalHeaders[1]}>
                            <AIRPNumberInput
                                    textAlign='center'
                                    initialValue={data[cellName as keyof ProfileAttributes] ? data[cellName as keyof ProfileAttributes]?.toString() : null}
                                    name={cellName}
                                    decimals={false}
                                    onChange={(value: number | null) => updateValue(value, cellName)}
                                />
                        </td>)}
                        <td headers={horizontalHeaders[7] + " " + verticalHeaders[1]}>{commaSeparate(calculateBasicBedTotal())}</td>
                    </tr>
                    <tr>
                        <td id={verticalHeaders[2]} className="vertical-header">{t(verticalHeaders[2])}</td>
                        {semiCellNames.map((cellName, index) => <td key={`row-2-${index}`} className={`bed-counts-input-cell ${hasError(errors, cellName) ? "table-error" : ""}`} headers={horizontalHeaders.slice(1, 7)[index] + " " + verticalHeaders[2]}>

                            <AIRPNumberInput
                                    textAlign='center'
                                    initialValue={data[cellName as keyof ProfileAttributes] ? data[cellName as keyof ProfileAttributes]?.toString() : null}
                                    name={cellName}
                                    decimals={false}
                                    onChange={(value: number | null) => updateValue(value, cellName)}/>
                        </td>)}
                        <td headers={horizontalHeaders[7] + " " + verticalHeaders[2]}>{commaSeparate(calculateSemiPrivateBedTotal())}</td>
                    </tr>
                    <tr>
                        <td id={verticalHeaders[3]} className="vertical-header">{t(verticalHeaders[3])}</td>
                        {privateCellNames.map((cellName, index) => <td key={`row-3-${index}`} className={`bed-counts-input-cell ${hasError(errors, cellName) ? "table-error" : ""}`} headers={horizontalHeaders.slice(1, 7)[index] + " " + verticalHeaders[3]}>
                            <AIRPNumberInput
                                    textAlign='center'
                                    initialValue={data[cellName as keyof ProfileAttributes] ? data[cellName as keyof ProfileAttributes]?.toString() : null}
                                    name={cellName}
                                    decimals={false}
                                    onChange={(value: number | null) => updateValue(value, cellName)}
                                />
                        </td>)}
                        <td headers={horizontalHeaders[7] + " " + verticalHeaders[3]}>{commaSeparate(calculatePrivateBedTotal())}</td>
                    </tr>
                    <tr>
                        <td id={verticalHeaders[4]} className="vertical-header">{t(verticalHeaders[4])}</td>
                        {otherCellNames.map((cellName, index) => <td key={`row-4-${index}`} className={`bed-counts-input-cell ${hasError(errors, cellName) ? "table-error" : ""}`} headers={horizontalHeaders.slice(1, 7)[index] + " " + verticalHeaders[4]}>
                            <AIRPNumberInput
                                    textAlign='center'
                                    initialValue={data[cellName as keyof ProfileAttributes] ? data[cellName as keyof ProfileAttributes]?.toString() : null}
                                    name={cellName}
                                    decimals={false}
                                    onChange={(value: number | null) => updateValue(value, cellName)}
                                />
                        </td>)}
                        <td headers={horizontalHeaders[7] + " " + verticalHeaders[4]}>{commaSeparate(calculateOtherBedTotal())}</td>
                    </tr>
                    <tr className="bed-counts-total-row">
                        <td id={verticalHeaders[5]} className="vertical-header">{t(verticalHeaders[5])}</td>
                        <td headers={horizontalHeaders[1] + " " + verticalHeaders[5]}>{commaSeparate(calculateAaaBedTotal())}</td>
                        <td headers={horizontalHeaders[2] + " " + verticalHeaders[5]}>{commaSeparate(calculateAaBedTotal())}</td>
                        <td headers={horizontalHeaders[3] + " " + verticalHeaders[5]}>{commaSeparate(calculateABedTotal())}</td>
                        <td headers={horizontalHeaders[4] + " " + verticalHeaders[5]}>{commaSeparate(calculateBBedTotal())}</td>
                        <td headers={horizontalHeaders[5] + " " + verticalHeaders[5]}>{commaSeparate(calculateCBedTotal())}</td>
                        <td headers={horizontalHeaders[6] + " " + verticalHeaders[5]}>{commaSeparate(calculateDBedTotal())}</td>
                        <td headers={horizontalHeaders[7] + " " + verticalHeaders[5]}>{commaSeparate(calculateTotal())}</td>
                    </tr>
                </tbody>
            </table>
        </>            
    )
}

export default BedCountsTable